import { useEffect, useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded';
import ArrowOutwardRounded from '@mui/icons-material/ArrowOutwardRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined';
import GitHub from '@mui/icons-material/GitHub';
import LightModeOutlined from '@mui/icons-material/LightModeOutlined';
import LinkedIn from '@mui/icons-material/LinkedIn';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import MenuRounded from '@mui/icons-material/MenuRounded';
import TerminalRounded from '@mui/icons-material/TerminalRounded';
import pp from './assets/images/pp.jpeg';
import { content, journey, packages, profile, projects, publicProjects, stack } from './data/portfolio';
import './Portfolio.css';

const reveal = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0 },
};

function Reveal({ children, className = '', delay = 0 }) {
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      className={className}
      initial={reduceMotion ? false : 'hidden'}
      whileInView={reduceMotion ? undefined : 'visible'}
      viewport={{ once: true, amount: 0.16 }}
      variants={reveal}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function SectionIntro({ kicker, title, copy, titleId }) {
  return (
    <Reveal className="section-intro">
      <p className="section-kicker">{kicker}</p>
      <h2 id={titleId}>{title}</h2>
      {copy && <p className="section-copy">{copy}</p>}
    </Reveal>
  );
}

function ExternalLink({ href, children, className = '' }) {
  return (
    <a className={`text-link ${className}`} href={href} target="_blank" rel="noreferrer">
      <span>{children}</span>
      <ArrowOutwardRounded fontSize="small" aria-hidden="true" />
    </a>
  );
}

function Header({ language, setLanguage, theme, setTheme, labels }) {
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  return (
    <header className="site-header">
      <a className="wordmark" href="#top" aria-label="Salih Öz — ana sayfa">
        SÖ<span>.</span>
      </a>

      <button className="icon-button menu-button" type="button" aria-label={labels.menu} aria-expanded={menuOpen} onClick={() => setMenuOpen((open) => !open)}>
        {menuOpen ? <CloseRounded /> : <MenuRounded />}
      </button>

      <nav className={menuOpen ? 'nav-links nav-open' : 'nav-links'} aria-label="Primary navigation">
        <a href="#about" onClick={closeMenu}>{labels.nav.about}</a>
        <a href="#services" onClick={closeMenu}>{labels.nav.services}</a>
        <a href="#work" onClick={closeMenu}>{labels.nav.work}</a>
        <a href="#open-source" onClick={closeMenu}>{labels.nav.openSource}</a>
        <a href="#experience" onClick={closeMenu}>{labels.nav.experience}</a>
        <a href="#contact" onClick={closeMenu}>{labels.nav.contact}</a>
      </nav>

      <div className="header-actions">
        <button className="utility-button" type="button" onClick={() => setLanguage(language === 'tr' ? 'en' : 'tr')} aria-label={labels.language}>
          {language === 'tr' ? 'EN' : 'TR'}
        </button>
        <button className="icon-button" type="button" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} aria-label={labels.theme}>
          {theme === 'dark' ? <LightModeOutlined /> : <DarkModeOutlined />}
        </button>
      </div>
    </header>
  );
}

function ProjectCard({ project, language, labels, index }) {
  const link = project.live || project.store || project.source;
  const VisualElement = link ? 'a' : 'div';
  const visualProps = link ? { href: link, target: '_blank', rel: 'noreferrer' } : {};
  const projectLinks = [
    project.live && { href: project.live, label: labels.live },
    project.store && { href: project.store, label: labels.store },
    project.source && { href: project.source, label: labels.source },
    project.doc && { href: project.doc, label: labels.document },
  ].filter(Boolean);

  return (
    <Reveal className="project-wrap">
      <article className={`project-card ${index % 2 ? 'project-reverse' : ''}`}>
        <VisualElement className={`project-visual project-visual--${project.presentation || 'landscape'}`} {...visualProps} aria-label={link ? `${project.title} — ${projectLinks[0].label}` : undefined}>
          <span className="visual-orbit visual-orbit-one" aria-hidden="true" />
          <span className="visual-orbit visual-orbit-two" aria-hidden="true" />
          {project.icon && <img className="project-app-icon" src={project.icon} alt={`${project.title} uygulama ikonu`} loading="lazy" />}
          <div className={`device-stage ${project.gallery ? 'device-gallery' : ''}`}>
            {project.gallery
              ? project.gallery.map((image, imageIndex) => <img className={`gallery-screen gallery-screen-${imageIndex + 1}`} src={image} alt={`${project.title} ekran görüntüsü ${imageIndex + 1}`} loading="lazy" key={image} />)
              : <img src={project.image} alt={`${project.title} arayüzü`} loading="lazy" />}
          </div>
          {link && <span className="visual-arrow"><ArrowOutwardRounded /></span>}
        </VisualElement>
        <div className="project-content">
          <div className="project-meta">
            <span>{labels.project} / {project.no}</span>
            <span>{project.type[language]}</span>
          </div>
          <h3>{project.title}</h3>
          <p>{project.description[language]}</p>
          {project.features && (
            <ul className="project-features">
              {project.features[language].map((feature) => <li key={feature}>{feature}</li>)}
            </ul>
          )}
          <p className="project-impact">↳ {project.impact[language]}</p>
          <ul className="tag-list" aria-label="Technologies">
            {project.technologies.map((technology) => <li key={technology}>{technology}</li>)}
          </ul>
          <div className="project-links">
            {projectLinks.map((item) => <ExternalLink href={item.href} key={item.href}>{item.label}</ExternalLink>)}
          </div>
        </div>
      </article>
    </Reveal>
  );
}

function PackageCard({ item, language, labels }) {
  const [copied, setCopied] = useState(false);

  const copyCommand = async () => {
    try {
      await navigator.clipboard.writeText(item.command);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <article className="package-card">
      <div className="package-topline">
        <TerminalRounded aria-hidden="true" />
        <span>v{item.version}</span>
      </div>
      <h3>{item.name}</h3>
      <p>{item.description[language]}</p>
      <button className="install-command" type="button" onClick={copyCommand} aria-label={`${labels.install}: ${item.command}`}>
        <code>{item.command}</code>
        <span>{copied ? labels.copied : <ContentCopyRounded fontSize="small" />}</span>
      </button>
      <ul className="package-tags" aria-label="Package categories">
        {item.tags.map((tag) => <li key={tag}>{tag}</li>)}
      </ul>
      <div className="package-links">
        <ExternalLink href={item.npm}>{labels.npm}</ExternalLink>
        {item.repository && <ExternalLink href={item.repository}>{labels.repository}</ExternalLink>}
      </div>
    </article>
  );
}

function Portfolio({ initialLanguage = 'tr', initialTheme = 'dark' }) {
  const [language, setLanguage] = useState(initialLanguage);
  const [theme, setTheme] = useState(initialTheme);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const labels = useMemo(() => content[language], [language]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      try {
        const savedLanguage = localStorage.getItem('portfolio-language');
        const savedTheme = localStorage.getItem('portfolio-theme');

        if (savedLanguage === 'tr' || savedLanguage === 'en') setLanguage(savedLanguage);
        if (savedTheme === 'dark' || savedTheme === 'light') setTheme(savedTheme);
      } catch {
        // Storage can be unavailable in privacy-focused browser contexts.
      } finally {
        setPreferencesLoaded(true);
      }
    }, 0);

    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.lang = language;
    document.title = labels.seo.title;

    const setMetaContent = (selector, value) => {
      document.querySelector(selector)?.setAttribute('content', value);
    };

    setMetaContent('meta[name="description"]', labels.seo.description);
    setMetaContent('meta[property="og:title"]', labels.seo.title);
    setMetaContent('meta[property="og:description"]', labels.seo.description);
    setMetaContent('meta[property="og:locale"]', labels.seo.locale);
    setMetaContent('meta[name="twitter:title"]', labels.seo.title);
    setMetaContent('meta[name="twitter:description"]', labels.seo.description);

    if (preferencesLoaded) {
      try {
        localStorage.setItem('portfolio-theme', theme);
        localStorage.setItem('portfolio-language', language);
      } catch {
        // The portfolio remains usable when persistent storage is unavailable.
      }
    }
  }, [labels, language, preferencesLoaded, theme]);

  useEffect(() => {
    if (!window.location.hash) return;

    const timer = window.setTimeout(() => {
      const target = document.querySelector(window.location.hash);
      if (!target) return;

      const previousBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      target.scrollIntoView({ block: 'start' });
      document.documentElement.style.scrollBehavior = previousBehavior;
    }, 120);

    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className="portfolio-shell" id="top">
      <a className="skip-link" href="#main">Skip to content</a>
      <Header language={language} setLanguage={setLanguage} theme={theme} setTheme={setTheme} labels={labels} />

      <main id="main">
        <section className="hero section-pad" aria-labelledby="hero-title">
          <div className="hero-grid" aria-hidden="true" />
          <motion.div className="hero-copy" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
            <motion.p className="hero-eyebrow" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <span className="status-dot" />{labels.hero.eyebrow}
            </motion.p>
            <h1 id="hero-title">
              <motion.span initial={{ y: '105%' }} animate={{ y: 0 }} transition={{ duration: 0.75, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}>{labels.hero.title}</motion.span>
              <motion.span className="accent-line" initial={{ y: '105%' }} animate={{ y: 0 }} transition={{ duration: 0.75, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}>{labels.hero.titleAccent}</motion.span>
              <motion.span initial={{ y: '105%' }} animate={{ y: 0 }} transition={{ duration: 0.75, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}>{labels.hero.titleEnd}</motion.span>
            </h1>
            <div className="hero-bottom">
              <p>{labels.hero.intro}</p>
              <div className="hero-actions">
                <a className="button button-primary" href="#work">{labels.hero.primaryCta}<ArrowDownwardRounded /></a>
                <a className="button button-ghost" href={`mailto:${profile.email}`}>{labels.hero.secondaryCta}<ArrowOutwardRounded /></a>
              </div>
            </div>
          </motion.div>

          <motion.aside className="hero-profile" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.35 }}>
            <div className="portrait-frame"><img src={pp} alt="Salih Öz" /></div>
            <div className="profile-note">
              <span className="status-dot" />
              <p>{labels.hero.availability}</p>
            </div>
            <div className="profile-location"><span>{profile.location[language]}</span><span>GMT+3</span></div>
          </motion.aside>

          <a className="scroll-cue" href="#about"><span>{labels.hero.scroll}</span><ArrowDownwardRounded /></a>
        </section>

        <section className="stats-bar" aria-label="Highlights">
          {labels.stats.map((stat) => (
            <div className="stat" key={stat.label}><strong>{stat.value}</strong><span>{stat.label}</span></div>
          ))}
        </section>

        <section className="about section-pad" id="about">
          <SectionIntro kicker={labels.about.kicker} title={labels.about.title} />
          <div className="about-grid">
            <Reveal className="about-copy">
              {labels.about.body.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              <div className="social-row">
                <ExternalLink href={profile.github}>GitHub</ExternalLink>
                <ExternalLink href={profile.linkedin}>LinkedIn</ExternalLink>
                <ExternalLink href={profile.npm}>npm</ExternalLink>
                <ExternalLink href={profile.resume}>CV / Résumé</ExternalLink>
              </div>
            </Reveal>
            <div className="principles">
              {labels.about.principles.map((item, index) => (
                <Reveal className="principle" delay={index * 0.07} key={item.no}>
                  <span>{item.no}</span><div><h3>{item.title}</h3><p>{item.text}</p></div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal className="stack-marquee">
            <p>CORE TOOLKIT</p>
            <div>{stack.map((item) => <span key={item}>{item}</span>)}</div>
          </Reveal>
        </section>

        <section className="services section-pad" id="services" aria-labelledby="services-title">
          <SectionIntro kicker={labels.services.kicker} title={labels.services.title} copy={labels.services.intro} titleId="services-title" />
          <div className="service-grid">
            {labels.services.items.map((service, index) => (
              <Reveal className="service-card" delay={index * 0.06} key={service.title}>
                <span className="service-number">0{index + 1}</span>
                <div>
                  <h3>{service.title}</h3>
                  <p>{service.text}</p>
                </div>
                <ul className="service-tags" aria-label={`${service.title} technologies`}>
                  {service.tags.map((tag) => <li key={tag}>{tag}</li>)}
                </ul>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="work section-pad" id="work">
          <SectionIntro kicker={labels.work.kicker} title={labels.work.title} />
          <div className="project-list">
            {projects.map((project, index) => <ProjectCard project={project} language={language} labels={labels.work} index={index} key={project.title} />)}
          </div>
        </section>

        <section className="open-source section-pad" id="open-source">
          <SectionIntro kicker={labels.openSource.kicker} title={labels.openSource.title} copy={labels.openSource.intro} />
          <Reveal className="package-grid">
            {packages.map((item) => <PackageCard item={item} language={language} labels={labels.openSource} key={item.name} />)}
          </Reveal>

          <Reveal className="public-projects">
            <div className="subsection-title"><p>{labels.openSource.moreProjects}</p><ExternalLink href={profile.github}>{labels.openSource.allRepos}</ExternalLink></div>
            <div className="repo-list">
              {publicProjects.map((project) => (
                <a href={project.href} target="_blank" rel="noreferrer" key={project.name}>
                  <div><h3>{project.name}</h3><p>{project.description[language]}</p></div>
                  <span>{project.language}</span><ArrowOutwardRounded />
                </a>
              ))}
            </div>
          </Reveal>
        </section>

        <section className="experience section-pad" id="experience">
          <SectionIntro kicker={labels.experience.kicker} title={labels.experience.title} />
          <div className="timeline">
            {journey.map((item, index) => (
              <Reveal className="timeline-item" delay={index * 0.06} key={`${item.company}-${item.period.en}`}>
                <p className="timeline-period">{item.period[language]}</p>
                <div className="timeline-main"><h3>{item.role[language]}</h3><p className="timeline-company">{item.company}</p><p>{item.description[language]}</p></div>
                <ul className="tag-list">{item.tags.map((tag) => <li key={tag}>{tag}</li>)}</ul>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="contact section-pad" id="contact">
          <Reveal>
            <p className="section-kicker">{labels.contact.kicker}</p>
            <h2>{labels.contact.title} <em>{labels.contact.titleAccent}</em> {labels.contact.titleEnd}</h2>
            <div className="contact-bottom">
              <p>{labels.contact.body}</p>
              <div className="contact-actions">
                <a className="button button-primary" href={`mailto:${profile.email}`}><MailOutlineRounded />{labels.contact.email}</a>
                <a className="button button-ghost" href={profile.linkedin} target="_blank" rel="noreferrer"><LinkedIn />{labels.contact.linkedin}</a>
              </div>
            </div>
          </Reveal>
        </section>
      </main>

      <footer className="site-footer">
        <p>© {new Date().getFullYear()} {profile.name}</p>
        <p>{labels.footer} <span>Salih Öz</span></p>
        <div><a href={profile.github} aria-label="GitHub"><GitHub /></a><a href={profile.linkedin} aria-label="LinkedIn"><LinkedIn /></a></div>
      </footer>

    </div>
  );
}

export default Portfolio;
